// The ladder's last rung, shown once a question closes in Pink: the question
// again in small steps, then CWY's own scaffold for its sub-topic. Encouraging,
// never punitive — nothing here is scored.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { apiGet } from '@/lib/api';
import type { ExamScaffoldDTO, ScaffoldSteps } from '@/types';
import { ScaffoldIcon } from '@/components/exam/icons';
import { MathText } from '@/components/exam/MathText';

interface ScaffoldRevealProps {
  spId: string;
  questionId: string;
  onContinue: () => void;
}

export function ScaffoldReveal({ spId, questionId, onContinue }: ScaffoldRevealProps) {
  // Two asks, as the theory drawer makes: the authored scaffold is a read, the
  // steps are written by a model and can be slow or absent. Neither waits on
  // the other.
  const authored = useQuery({
    queryKey: ['exam-scaffold', spId, questionId],
    staleTime: Infinity,
    queryFn: () =>
      apiGet<{ scaffold: ExamScaffoldDTO }>(`/student-papers/${spId}/scaffold/${questionId}`),
  });
  const forQuestion = useQuery({
    queryKey: ['exam-scaffold-steps', spId, questionId],
    staleTime: Infinity,
    retry: false,
    queryFn: () =>
      apiGet<{ steps: ScaffoldSteps | null }>(
        `/student-papers/${spId}/scaffold-steps/${questionId}`,
      ),
  });

  const scaffold = authored.data?.scaffold ?? null;
  const levels = scaffold?.levels ?? [];
  const steps = forQuestion.data?.steps ?? null;
  const loading = authored.isLoading || forQuestion.isLoading;

  return (
    // `bg-pink-50` was a raw Tailwind tint, so it stayed light while the themed
    // ink above it went near-white — the heading vanished into the panel. The
    // bin ramp re-points with the theme; the literal never could.
    <div className="flex flex-col gap-4 rounded-2xl border border-bin-pink-border bg-bin-pink-bg p-5">
      <div className="flex flex-col items-center gap-2 text-center">
        <motion.div
          initial={{ scale: 0.6, rotate: -8, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          // Was `var(--bin-pink-text)`, which is not a token — the ramp stop is
          // `--pink-text`. Undefined, so the icon silently inherited body ink.
          style={{ color: 'var(--pink-text)' }}
        >
          <ScaffoldIcon className="h-10 w-10" />
        </motion.div>
        <p className="text-lg font-bold text-bin-pink-text">We'll get there!</p>
        <p className="max-w-sm text-sm text-slate-600">
          Scaffolding unlocked. Here is this question again in smaller steps — climb them one at a
          time.
        </p>
      </div>

      {loading && (
        <p className="animate-pulse rounded-xl bg-card p-3 text-center text-xs text-slate-500">
          Breaking this question into steps…
        </p>
      )}

      {steps && <StepLadder steps={steps} />}

      {levels.length > 0 && (
        <section className="rounded-[14px] border border-slate-200 bg-card p-4">
          <p className="text-fs-10 font-bold uppercase tracking-[0.08em] text-slate-500">
            From the basics · {scaffold?.subtopicName}
          </p>
          <ol className="mt-2 flex flex-col gap-3">
            {levels.map((level) => (
              <li key={level.level}>
                <p className="text-sm font-semibold text-ink">Rung {level.level + 1}</p>
                <div className="mt-1 space-y-1.5 text-sm leading-relaxed text-slate-700">
                  {level.theoryNote.split(/\n{2,}/).map((para, i) => (
                    <p key={i}>
                      <MathText>{para}</MathText>
                    </p>
                  ))}
                </div>
                {level.workedExamples.map((example, i) =>
                  example.step || example.explanation ? (
                    <div
                      key={i}
                      className="mt-2 rounded-lg bg-slate-50 p-2.5 text-sm text-slate-700"
                    >
                      {example.step && (
                        <p className="font-semibold">
                          <MathText>{example.step}</MathText>
                        </p>
                      )}
                      {example.explanation && (
                        <p>
                          <MathText>{example.explanation}</MathText>
                        </p>
                      )}
                    </div>
                  ) : null,
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {!loading && (
        <p className="text-center text-xs text-slate-600">
          {!steps && levels.length === 0 && 'No steps are written for this one yet. '}
          After you submit, answer it again from your Cards — the AI tutor is there too.
        </p>
      )}

      <button
        type="button"
        onClick={onContinue}
        className="self-center rounded-xl bg-slate-900 px-5 py-2.5 font-semibold text-white transition hover:bg-slate-800"
      >
        Back to questions
      </button>
    </div>
  );
}

/** The steps one at a time: each opens only when the student asks for it. */
function StepLadder({ steps }: { steps: ScaffoldSteps }) {
  const [shown, setShown] = useState(1);
  const all = steps.steps;

  return (
    <section className="rounded-[14px] border border-slate-200 bg-card p-4">
      <p className="text-fs-10 font-bold uppercase tracking-[0.08em] text-slate-500">
        Step by step
      </p>
      <h4 className="mt-1 text-sm font-semibold text-ink">
        <MathText>{steps.title}</MathText>
      </h4>
      <ol className="mt-3 flex flex-col gap-2.5">
        {all.slice(0, shown).map((step, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            className="flex gap-3"
          >
            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-bin-pink-border bg-bin-pink-bg text-xs font-bold text-bin-pink-text">
              {i + 1}
            </span>
            <div className="text-sm">
              <p className="font-semibold text-ink">
                <MathText>{step.title}</MathText>
              </p>
              <p className="mt-0.5 leading-relaxed text-slate-700">
                <MathText>{step.detail}</MathText>
              </p>
            </div>
          </motion.li>
        ))}
      </ol>
      {shown < all.length ? (
        <button
          type="button"
          onClick={() => setShown((n) => n + 1)}
          className="mt-3 w-full rounded-xl border border-slate-200 bg-card px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Show step {shown + 1} of {all.length}
        </button>
      ) : (
        <p className="mt-3 text-xs text-slate-500">Now work the last step out yourself.</p>
      )}
    </section>
  );
}
